import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Package,
  Plus,
  Search,
  RotateCcw,
  Boxes,
  Layers,
  X,
} from 'lucide-react';
import {
  useMaterials,
  useCreateMaterial,
  type NewMaterial,
  type MaterialRow,
} from '../lib/directory';
import { useUpdateMaterial, useDeleteMaterial } from '../lib/directory-edit';
import { useLookups, getStandardMaterialUnitOptions } from '../lib/lookups';
import { useSession } from '../lib/session';
import { supabase } from '../lib/supabase';
import Shell from '../components/Shell';
import Confirm, { useConfirm } from '../components/Confirm';
import { useRestore } from '../lib/restore';
import { guardMaterial } from '../lib/delete-guards';
import RowActions from '../components/RowActions';
import {
  Drawer,
  Field,
  EmptyState,
  TableSkeleton,
  ErrorState,
  initials,
  useToast,
  Select,
} from '../components/ui';

export default function Materials() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<MaterialRow | null>(null);
  const confirm = useConfirm<MaterialRow>();
  const restore = useRestore();
  const remove = useDeleteMaterial();
  const toast = useToast();
  const { data, isLoading, error, refetch } = useMaterials(search);

  async function doDelete() {
    if (!confirm.target) return;
    try {
      await remove.mutateAsync(confirm.target.id);
      const removed = confirm.target;
      toast.showWithAction(removed.name + ' removed', {
        label: 'Undo',
        run: () => restore.mutateAsync({ table: 'materials', id: removed.id }),
      });
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not remove the material', true);
    } finally {
      confirm.close();
    }
  }

  // Summary KPI calculations
  const totalItems = data?.length ?? 0;
  const uniqueUnits = new Set(data?.map((m) => m.unitName).filter(Boolean)).size;

  return (
    <Shell
      title="Materials & Inventory"
      crumb="Inventory"
      actions={
        <button
          className="btn btn-primary btn-auto btn-sm"
          onClick={() => setAdding(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
        >
          <Plus size={15} strokeWidth={2.2} />
          <span>Add Material</span>
        </button>
      }
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        {/* KPI Metrics Summary */}
        <div className="kpi-row" style={{ marginBottom: 20, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 280px))' }}>
          <div className="kpi">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <span className="lbl">Total Materials</span>
              <div style={{ color: 'var(--brand)', background: 'rgba(37, 99, 235, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                <Package size={14} />
              </div>
            </div>
            <div className="val num" style={{ fontSize: 20 }}>{totalItems}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 4 }}>Catalogued materials in ERP</div>
          </div>

          <div className="kpi">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <span className="lbl">Measurement Units</span>
              <div style={{ color: '#059669', background: 'rgba(5, 150, 105, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                <Boxes size={14} />
              </div>
            </div>
            <div className="val num" style={{ fontSize: 20, color: '#059669' }}>{uniqueUnits}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 4 }}>Configured units in use</div>
          </div>
        </div>

        {/* Search & Filter Toolbar */}
        <div
          className="toolbar"
          style={{
            background: 'var(--surface)',
            padding: '10px 14px',
            borderRadius: 'var(--radius)',
            border: '1px solid var(--border)',
            boxShadow: 'var(--shadow-xs)',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <div className="field-inline" style={{ width: 280, maxWidth: '100%', flex: 'none' }}>
            <Search size={14} style={{ color: 'var(--text-faint)' }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search materials by name or category..."
              aria-label="Search materials"
            />
          </div>

          {search && (
            <button
              className="btn btn-auto btn-sm btn-ghost"
              onClick={() => setSearch('')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                color: 'var(--text-soft)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              <RotateCcw size={13} />
              <span>Clear</span>
            </button>
          )}
        </div>

        {/* Center-Aligned Materials Directory Table */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            {isLoading ? (
              <TableSkeleton rows={6} cols={3} />
            ) : error ? (
              <ErrorState error={error} onRetry={() => void refetch()} />
            ) : !data?.length ? (
              <EmptyState
                icon="box"
                title={search ? 'No materials match that search' : 'No materials registered'}
                hint={
                  search
                    ? 'Try searching with different keywords or check spelling.'
                    : 'Add construction materials so purchases and site usage can be tracked.'
                }
              />
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Material Details</th>
                    <th>Unit</th>
                    <th style={{ width: 80, textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((m) => (
                    <tr
                      key={m.id}
                      className="clickable"
                      onClick={() => navigate(`/materials/${m.id}`)}
                    >
                      <td>
                        <div
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 12,
                            textAlign: 'left',
                            maxWidth: 320,
                          }}
                        >
                          <div
                            className="avatar-sq"
                            style={{
                              width: 38,
                              height: 38,
                              fontSize: 13,
                              fontWeight: 700,
                              borderRadius: 'var(--radius-sm)',
                              background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.08), rgba(37, 99, 235, 0.02))',
                              color: 'var(--brand)',
                              border: '1px solid rgba(37, 99, 235, 0.15)',
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {initials(m.name)}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div
                              className="name"
                              style={{
                                fontSize: 13.5,
                                fontWeight: 600,
                                color: 'var(--text)',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {m.name}
                            </div>
                            {m.brand && (
                              <div
                                style={{
                                  fontSize: 11.5,
                                  color: 'var(--text-faint)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  marginTop: 2,
                                }}
                              >
                                <Layers size={10} />
                                <span>{m.brand}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      <td>
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            padding: '2px 8px',
                            borderRadius: 'var(--radius-xs)',
                            background: 'var(--surface-muted)',
                            border: '1px solid var(--border)',
                            color: 'var(--text-soft)',
                          }}
                        >
                          {m.unitName ?? '—'}
                        </span>
                      </td>

                      <td onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <RowActions
                            label={m.name}
                            onEdit={() => setEditing(m)}
                            onDelete={() => confirm.ask(m)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </motion.div>

      {adding && <MaterialDrawer onClose={() => setAdding(false)} />}
      {editing && <MaterialDrawer existing={editing} onClose={() => setEditing(null)} />}

      {confirm.target && (
        <Confirm
          title={'Delete ' + confirm.target.name + '?'}
          message="It will disappear from your material list. You can undo this straight away from the notification that appears."
          check={guardMaterial(confirm.target.id)}
          busy={remove.isPending}
          onConfirm={() => void doDelete()}
          onCancel={confirm.close}
        />
      )}
    </Shell>
  );
}

const EMPTY: NewMaterial = {
  name: '',
  brand: '',
  unit_id: '',
  rateRupees: '',
  supplier_id: '',
  opening_qty: '',
};

/** One drawer for both Add and Edit Material */
export function MaterialDrawer({
  existing,
  onClose,
}: {
  existing?: MaterialRow;
  onClose: () => void;
}) {
  const [form, setForm] = useState<NewMaterial>(
    existing
      ? {
          name: existing.name,
          brand: existing.brand ?? '',
          unit_id: existing.unitId ?? '',
          // Paise back to rupees, so the field reads as it was typed.
          rateRupees: String(existing.ratePaise / 100),
          supplier_id: existing.supplierId ?? '',
          opening_qty: String(existing.openingQty),
        }
      : EMPTY,
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [customUnitOpen, setCustomUnitOpen] = useState(false);
  const [customUnitName, setCustomUnitName] = useState('');
  const [isCreatingUnit, setIsCreatingUnit] = useState(false);

  const actor = useSession((s) => s.actor);
  const qc = useQueryClient();
  const { data: lk, isLoading } = useLookups();
  const create = useCreateMaterial();
  const update = useUpdateMaterial();
  const toast = useToast();

  const set = (k: keyof NewMaterial) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const unitOptions = (lk?.units?.length || !isLoading)
    ? [
        { value: '', label: 'Select Unit' },
        ...getStandardMaterialUnitOptions(lk?.units),
        { value: '__other__', label: 'Other' },
      ]
    : [{ value: '', label: 'Loading units…' }];

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = 'Material name is required';
    setErrors(next);
    if (Object.keys(next).length) return;

    let finalUnitId = form.unit_id;

    // If custom unit box was open and user typed a name, auto-create it seamlessly
    if (customUnitOpen && customUnitName.trim() && actor) {
      setIsCreatingUnit(true);
      try {
        const trimmedName = customUnitName.trim();
        let name = trimmedName;
        let shortName = trimmedName.toLowerCase().slice(0, 10);
        const match = trimmedName.match(/^(.+?)\s*\((.+?)\)$/);
        if (match && match[1] && match[2]) {
          name = match[1].trim();
          shortName = match[2].trim().toLowerCase();
        }

        const found = lk?.units.find(
          (u) =>
            u.name.toLowerCase() === name.toLowerCase() ||
            u.short_name.toLowerCase() === shortName.toLowerCase(),
        );
        if (found) {
          finalUnitId = found.id;
        } else {
          const { data, error } = await supabase
            .from('units')
            .insert({
              company_id: actor.companyId,
              name,
              short_name: shortName,
              sort_order: 99,
              is_active: true,
            })
            .select('id, name, short_name')
            .single();

          if (error) throw error;
          finalUnitId = data.id;
          void qc.invalidateQueries({ queryKey: ['lookups'] });
        }
      } catch (err) {
        toast.show(err instanceof Error ? err.message : 'Could not save custom unit', true);
        setIsCreatingUnit(false);
        return;
      }
      setIsCreatingUnit(false);
    } else if (finalUnitId && finalUnitId.startsWith('__std__') && actor) {
      const stdName = finalUnitId.replace('__std__', '');
      const found = lk?.units?.find(
        (u) =>
          u.name.toLowerCase() === stdName.toLowerCase() ||
          u.short_name.toLowerCase() === stdName.toLowerCase(),
      );
      if (found) {
        finalUnitId = found.id;
      } else {
        try {
          const { data, error } = await supabase
            .from('units')
            .insert({
              company_id: actor.companyId,
              name: stdName,
              short_name: stdName.toLowerCase().slice(0, 10),
              sort_order: 50,
              is_active: true,
            })
            .select('id')
            .single();
          if (error) throw error;
          finalUnitId = data.id;
          void qc.invalidateQueries({ queryKey: ['lookups'] });
        } catch {
          // ignore error
        }
      }
    }

    try {
      if (existing) {
        await update.mutateAsync({ ...form, unit_id: finalUnitId, id: existing.id });
        toast.show(`${form.name} updated successfully`);
      } else {
        await create.mutateAsync({ ...form, unit_id: finalUnitId, rateRupees: '0', supplier_id: '', opening_qty: '0' });
        toast.show(`${form.name} added to inventory`);
      }
      onClose();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not save the material', true);
    }
  }

  return (
    <Drawer
      title={existing ? 'Edit Material' : 'Add Material'}
      description={existing ? existing.name : 'Create a new construction material record'}
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel={existing ? 'Save Changes' : 'Create Material'}
      busy={create.isPending || update.isPending || isCreatingUnit}
    >
      <div className="form-grid">
        <Field label="Material Name" error={errors.name} span2>
          <input
            value={form.name}
            onChange={set('name')}
            autoFocus
            aria-invalid={!!errors.name}
          />
        </Field>

        <Field
          label={customUnitOpen ? 'Measurement Unit (Custom)' : 'Measurement Unit'}
          optional
          span2
          hint={customUnitOpen ? 'Type custom unit name (e.g. Bundle, Box)' : undefined}
        >
          {!customUnitOpen ? (
            <Select
              value={form.unit_id ?? ''}
              onChange={(val) => {
                if (val === '__other__') {
                  setCustomUnitOpen(true);
                  setCustomUnitName('');
                } else {
                  set('unit_id')({ target: { value: val } } as any);
                }
              }}
              disabled={isLoading}
              options={unitOptions}
            />
          ) : (
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <input
                value={customUnitName}
                onChange={(e) => setCustomUnitName(e.target.value)}
                autoFocus
                style={{ paddingRight: 36 }}
              />
              <button
                type="button"
                onClick={() => {
                  setCustomUnitOpen(false);
                  setCustomUnitName('');
                }}
                style={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-faint)',
                  cursor: 'pointer',
                  padding: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 4,
                }}
                title="Cancel & switch back to dropdown"
              >
                <X size={15} />
              </button>
            </div>
          )}
        </Field>
      </div>
    </Drawer>
  );
}
